import type { ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useAuth } from "../contexts/AuthContext";
import { Icon, preloadIcons } from "../components/Icon/Icon";
import { AppTooltip } from "../components/Tooltip/AppTooltip";
import { ResponsiveAvatar } from "@/shared/components/Avatar/ResponsiveAvatar";
import creatorverseLogoUrl from "@/assets/icons/Creatorverse_Icon_Gradient.png?url";
import { canAccessAdmin } from "@/api/user";
import { useMaxWidth } from "@/shared/hooks/useMaxWidth";
import { useLogout } from "@/shared/hooks/useLogout";
import {
	MOBILE_NAV_HEIGHT,
	PHONE_MAX_WIDTH,
	SAFE_AREA,
	TAP_TARGET,
	TAP_TARGET_PRIMARY,
	mediaPhone,
} from "@/shared/theme/breakpoints";
import { pressedDarken } from "@/shared/theme/pressed";
import { isProdStage } from "@/shared/utils/stage";

const ICON_SIZE = 24;
const DEFAULT_AVATAR = "/default_avatar.png";
// Cortex Discovery is not ready for clients yet, so the Explore entry only
// becomes a live link to it off production (local, app-test, demo). On prod
// Explore stays the disabled placeholder it is today.
const CORTEX_DISCOVERY_URL = "https://cortex.thecreatorverse.co/discovery/app/creator-discovery";

// Start fetching the nav's icons and images while the app boots (auth check,
// route chunk), so the nav renders complete instead of filling in piece by piece.
preloadIcons(["bell", "menuExplore", "menuDashboard", "menuCampaign", "menuSearch", "menuLogout", "setting"]);
for (const src of [creatorverseLogoUrl, DEFAULT_AVATAR]) new Image().src = src;
// Desktop: a fixed-width left rail. Phone: a fixed bottom bar.
const NavigationContainer = styled.div`
	width: 72px;
	height: 100vh;
	min-height: 100vh;
	background: #fafafa;
	box-shadow: 0px 4px 12px 0px #1559a51f;
	display: flex;
	flex-direction: column;
	align-items: center;
	padding: 10px 0 0 0;
	position: fixed;
	top: 0;
	left: ${SAFE_AREA.left};
	margin-left: 8px;
	box-sizing: border-box;
	z-index: 2000;
	gap: 8px;

	&::before {
		content: "";
		position: absolute;
		left: -10px;
		top: 0;
		width: 10px;
		height: 100%;
		min-height: 100vh;
		background: linear-gradient(180deg, #4bc9f1 0%, #4460ef 33%, #3a0ca3 66%, #f72586 100%);
	}

	${mediaPhone`
		width: 100%;
		height: calc(${MOBILE_NAV_HEIGHT}px + ${SAFE_AREA.bottom});
		min-height: 0;
		flex-direction: row;
		justify-content: space-evenly;
		top: auto;
		bottom: 0;
		left: 0;
		margin-left: 0;
		padding: 0 calc(8px + ${SAFE_AREA.right}) ${SAFE_AREA.bottom} calc(8px + ${SAFE_AREA.left});
		gap: 0;
		box-shadow: 0px -4px 12px 0px #1559a51f;

		&::before {
			left: 0;
			top: 0;
			width: 100%;
			height: 4px;
			min-height: 0;
			background: linear-gradient(90deg, #4bc9f1 0%, #4460ef 33%, #3a0ca3 66%, #f72586 100%);
		}
	`}
`;
const LogoContainer = styled.div`
	cursor: pointer;
	display: flex;
	padding: 22px 16px;
	height: 64px;
	justify-content: center;
	width: 100%;

	/* The Campaigns item already goes to the same place on phones. */
	${mediaPhone`display: none;`}
`;

const MenuContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	width: 100%;
	align-items: center;

	/* Let the bell, menu items and avatar sit directly in the bottom bar's row. */
	${mediaPhone`display: contents;`}
`;

const MenuItemContainer = styled.button<{ $active?: boolean; $disabled?: boolean }>`
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
	padding: 16px;
	gap: 4px;
	border-radius: 50%;
	transition: all 0.3s ease;
	border: none;
	outline: none;
	background: ${(props) => (props.$active ? "#FFE5F0" : "transparent")};
	color: ${(props) => (props.$disabled ? "#b9b9b9" : props.$active ? "#F72586" : "#454545")};
	opacity: ${(props) => (props.$disabled ? 0.5 : 1)};

	&:hover {
		box-shadow: ${(props) => (props.$disabled ? "none" : props.$active ? "none" : "0px 4px 12px 0px #1559A50A")};
		background: ${(props) => (props.$disabled ? "transparent" : props.$active ? "#FFE5F0" : "#ffffff")};
	}

	&:focus-visible {
		box-shadow: ${(props) => (props.$disabled ? "none" : "0 0 0 2px #f72586")};
	}

	${mediaPhone`
		width: ${TAP_TARGET_PRIMARY}px;
		height: ${TAP_TARGET_PRIMARY}px;
		flex: 0 0 ${TAP_TARGET_PRIMARY}px;
		padding: 12px;
	`}

	/* The bottom bar has no room for items that can't be used yet. */
	${(props) => props.$disabled && mediaPhone`display: none;`}

	${pressedDarken}
`;

const MenuIcon = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;

	.anticon,
	img {
		font-size: 28px;
		width: 28px;
		height: 28px;
	}
`;

const LogoImage = styled.img`
	width: 34px;
	height: 20px;
`;

const UserProfileContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: center;
	gap: 12px;
	margin-top: 18px;

	${mediaPhone`
		width: ${TAP_TARGET_PRIMARY}px;
		flex: 0 0 ${TAP_TARGET_PRIMARY}px;
		margin-top: 0;
	`}
`;

const LogoutContainer = styled.div`
	margin-top: auto;
	margin-bottom: 30px;
	width: 100%;
	display: flex;
	justify-content: center;

	/* Phones: Log out lives in the account page's menu, not the bottom bar. */
	${mediaPhone`
		display: none;
	`}
`;

const UserAvatar = styled.button<{ $active: boolean }>`
	width: 40px;
	height: 40px;
	padding: 0;
	border: 2px solid ${(props) => (props.$active ? "#f72586" : "transparent")};
	border-radius: 50%;
	background: #f0f0f0;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	cursor: pointer;
	transition: box-shadow 0.3s ease;

	&:hover {
		box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
	}

	/* A full tap target in the bottom bar. */
	${mediaPhone`
		width: ${TAP_TARGET}px;
		height: ${TAP_TARGET}px;
	`}

	${pressedDarken}
`;

const UserAvatarImage = styled(ResponsiveAvatar)`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const LogoutButton = styled.button`
	width: 56px;
	height: 56px;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: all 0.3s ease;
	background: transparent;
	border-radius: 50%;
	padding: 16px;
	border: none;
	outline: none;

	&:hover {
		box-shadow: 0px 4px 12px 0px #1559a50a;
		background: #ffffff;
	}

	&:focus-visible {
		box-shadow: 0 0 0 2px #f72586;
	}

	${mediaPhone`
		width: ${TAP_TARGET_PRIMARY}px;
		height: ${TAP_TARGET_PRIMARY}px;
		padding: 12px;
	`}

	${pressedDarken}
`;

export const Navigation = ({ menuExtra }: { menuExtra?: ReactNode } = {}) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useAuth();
	const handleLogout = useLogout();
	// Tooltips can't open to the right of a bottom bar.
	const tooltipPlacement = useMaxWidth(PHONE_MAX_WIDTH) ? "top" : "right";

	const cortexDiscovery = !isProdStage();

	const allMenuItems = [
		// Ticket-348: admins and Finance-subrole agency users (country config, payments).
		...(canAccessAdmin(user)
			? [
					{
						key: "admin",
						icon: <Icon src="/icon/setting.svg" size={21} />,
						label: "Admin",
						path: "/admin",
					},
				]
			: []),
		{
			key: "explore",
			icon: <Icon name="menuExplore" size={ICON_SIZE} />,
			label: cortexDiscovery ? "Discovery" : "Explore",
			path: cortexDiscovery ? CORTEX_DISCOVERY_URL : "/tiktok/tto/discover-creator",
		},
		{
			key: "dashboard",
			icon: <Icon name="menuDashboard" size={ICON_SIZE} />,
			label: "Data Dashboard",
			path: "/data-dashboard",
		},
		{
			key: "campaign",
			icon: <Icon name="menuCampaign" size={ICON_SIZE} />,
			label: "Campaign",
			path: "/campaigns",
		},
		{
			key: "search",
			icon: <Icon name="menuSearch" size={ICON_SIZE} />,
			label: "Search",
			path: "/tiktok/similar-creator",
		},
	];

	const menuItems = allMenuItems.map((item) => ({
		...item,
		disabled: item.key !== "campaign" && item.key !== "admin" && !(cortexDiscovery && item.key === "explore"),
	}));

	const handleLogoClick = () => {
		navigate("/campaigns");
	};

	const isActive = (path: string) => {
		return location.pathname.startsWith(path);
	};

	return (
		<NavigationContainer>
			<LogoContainer onClick={handleLogoClick}>
				<LogoImage src={creatorverseLogoUrl} alt="Creatorverse" />
			</LogoContainer>

			<MenuContainer>
				{user && menuExtra}
				{menuItems.map((item) => (
					<AppTooltip key={item.key} title={item.label} placement={tooltipPlacement} arrow={false}>
						<MenuItemContainer
							$active={isActive(item.path)}
							$disabled={item.disabled}
							aria-label={item.label}
							aria-disabled={item.disabled || undefined}
							onClick={(e) => {
								if (item.disabled) return;
								if (item.path.startsWith("https://")) {
									window.open(item.path, "_blank", "noopener,noreferrer");
									return;
								}
								const url =
									item.path +
									(item.key === "explore"
										? "?page=1"
										: item.key === "dashboard"
											? "?handleName=thisisamplify"
											: "");
								if (e.ctrlKey || e.metaKey) {
									window.open(url, "_blank");
								} else {
									navigate(url);
								}
							}}
						>
							<MenuIcon>{item.icon}</MenuIcon>
						</MenuItemContainer>
					</AppTooltip>
				))}

				{user && (
					<UserProfileContainer>
						<AppTooltip title={user.name} placement={tooltipPlacement} arrow={false}>
							<UserAvatar
								$active={isActive("/account")}
								onClick={() => navigate("/account")}
								aria-label="Open my account"
							>
								{user.image ? (
									<UserAvatarImage displaySize={40} src={user.image} alt={user.name} />
								) : (
									<UserAvatarImage displaySize={40} src={DEFAULT_AVATAR} alt={user.name} />
								)}
							</UserAvatar>
						</AppTooltip>
					</UserProfileContainer>
				)}
			</MenuContainer>

			{user && (
				<LogoutContainer>
					<AppTooltip title="Logout" placement={tooltipPlacement} arrow={false}>
						<LogoutButton onClick={handleLogout} aria-label="Log out">
							<MenuIcon>
								<Icon name="menuLogout" size={ICON_SIZE} />
							</MenuIcon>
						</LogoutButton>
					</AppTooltip>
				</LogoutContainer>
			)}
		</NavigationContainer>
	);
};
